import { _ } from "../../../../core/polyglot/polyglot.js";
import { infoCircleIcon } from "../../../../core/elements/icons/index.js";
import { large } from "../../../../core/elements/size/index.js";
import AccountingDocumentWidget from "../../../shared/documentInfrastructure/AccountingDocumentWidget.js";
import HelpWizard from "../../../../core/chrome/HelpWizard/HelpWizard.js";
import TitleWidget from "../../../../core/chrome/TitleWidget2.js";

/**
 * Render the document's title for the forecast editor.
 *
 * @param {Period} period - What exactly is this period is still
 *   unclear. We might just get rid of it. For now, it just looks nicer
 *   with it
 * @param {boolean} isInSaveSpaceMode - Indicates a display for small screen
 */
export default class ForecastEditorTitleWidget extends AccountingDocumentWidget {
	constructor({ period, isInSaveSpaceMode = false }) {
		super(...arguments);
		this._period = period;
		this._isInSaveSpaceMode = isInSaveSpaceMode;
	}

	setup(...rest) {
		super.setup(...rest);

		this._title = new TitleWidget({
			title: this._renderTitleOn.bind(this),
			subtitle: (html) => this._renderSubtitleOn(html),
		});
	}

	setSaveSpaceMode(isInSaveSpaceMode) {
		this._isInSaveSpaceMode = isInSaveSpaceMode;
		this.update();
	}

	setPeriod(period) {
		this._period = period;
		this.update();
	}

	startHelpWizard() {
		let stepsBuilder;
		const currentReport = this._documentState.getCurrentReport();

		if (!currentReport) return;

		// This code does something we should try to avoid: using `if`
		// to dispatch on the type of an object. I did it this way to
		// avoid adding UI code to models. Another solution would have
		// been to introduce a visitor but it would have made sense
		// only for the 3 reports and POs will soon want help texts
		// for the other tabs (e.g., assumptions and settings).
		if (currentReport.isIncomeStatement()) {
			stepsBuilder = this._makeHelpStepsForIncomeStatement.bind(this);
		} else if (currentReport.isBalanceSheet()) {
			stepsBuilder = this._makeHelpStepsForBalanceSheet.bind(this);
		} else {
			stepsBuilder = this._makeHelpStepsForCashFlowReport.bind(this);
		}

		const helpWizard = new HelpWizard({ stepsBuilder });

		helpWizard.start();
	}

	renderContentOn(html) {
		if (!this._title) return;

		html.div(
			{ class: "ForecastEditorTitleWidget" },
			this._title,
			(html) => {
				if (!this._shouldRenderHelpIcon()) return;

				html.div(
					{ class: "help-icon" },
					this._renderHelpIconOn.bind(this),
				);
			},
		);
	}

	_renderSubtitleOn(html) {
		if (this._isInSaveSpaceMode) {
			return;
		}

		html.render(
			_("Forecast based on actuals until {period}", {
				period: this._period.toSmartString(),
			}),
		);
	}

	_renderTitleOn(html) {
		html.render(
			this._documentState.getForecastTemplateName() ||
				_("Forecast editor"),
		);
	}

	_renderHelpIconOn(html) {
		html.div(
			{ id: "info-toggler" },
			html.a(
				{
					"data-toggle": "modal",
					"data-test": "help",
					click: this.startHelpWizard.bind(this),
				},
				infoCircleIcon({ size: large }),
			),
		);
	}

	_shouldRenderHelpIcon() {
		return this._documentState.getCurrentReport();
	}

	_makeHelpStepsForIncomeStatement() {
		return [
			{
				title: _("Welcome to the world of forecasting"),
				content: (html) =>
					html.div(
						html.p(
							_(
								"Together with historical bookkeeping data and trends, your added knowledge about future events will give you an idea about the company’s future position within 5 minutes. This enables you to initiate productive discussions with your management team and to take more informed decisions together.",
							),
						),
						html.p(
							_(
								"At the moment, we do not support extended fiscal year in forecast",
							),
						),
						html.p(
							_("Follow this short guide to get you started!"),
						),
					),
			},
			{
				title: _("Add your first prediction"),
				element: "predicted-clickable-account-cell",
				content: _(
					"This table shows future periods where you can add predictions on both group level as well as individual accounts. By clicking on a cell of the table you open up a variety of prediction rules to pick from.",
				),
			},
			{
				title: _("Know your past"),
				element: "current-period-clickable-cell",
				content: _(
					"Historical bookkeeping periods that will guide you in making the predictions, are shown in a grey tone. By scrolling to the left you can follow how the trends have evolved. Click one any grey cell to get more information about that specific account or group.",
				),
			},
			{
				title: _("Annual summary"),
				element: "card",
				content: _(
					"This is a summary of all your predictions in the profit and loss and the balance sheet and it will provide you with a predicted bottom line at the end of the fiscal year.",
				),
			},
			{
				title: _("Monthly trend"),
				element: "summary-current-period",
				visible: !this._isInSaveSpaceMode,
				content: _(
					"This is a monthly profit or loss indicator in green/red. Above it is a small bar chart showing the months income and costs. This provides a good overview of the monthly trend of the business.",
				),
			},
			{
				title: _("Profit and Loss"),
				element: "tab-report-income-statement",
				content: _(
					"The profit and loss tab is where you set rules and one off values and analyze the effect that this would have on the company’s performance.",
				),
			},
			{
				title: _("Balance Sheet"),
				element: "tab-report-balance-sheet",
				content: _(
					"The balance sheet tab is where you can make manual journals for future months and review the effect that this would have on the company’s performance.",
				),
			},
			{
				title: _("Cash Flow"),
				element: "tab-report-cash-flow-report",
				content: _(
					"The cash flow tab focus on cash at hand and flows of cash based on predictions made in the first two tabs.",
				),
			},
			{
				title: _("Assumptions"),
				element: "tab-assumptions",
				content: _(
					"The assumptions tab is a combined view of all the predictions made in the profit and loss and the balance sheet.",
				),
			},
			{
				title: _("Publish"),
				element: "publish",
				content: _(
					"This forecast template will roll forward with the same prediction rules applied as new months of bookkeeping are added to the system. In order to freeze the current view for future comparisons to actuals, you can “Publish” the forecast and give it a name. Client users are only able to view your published forecasts.",
				),
			},
		];
	}

	_makeHelpStepsForBalanceSheet() {
		return [
			{
				title: _("Balance sheet events"),
				element: "predicted-clickable-account-cell",
				content: (html) =>
					html.div(
						html.p(
							_(
								"Predicted movements of assets and liabilities can be applied from the balance sheet tab.",
							),
						),
						html.p(
							_(
								"Click on an account cell to create manual journals.",
							),
						),
					),
			},
			{
				title: _("Financial stability"),
				element: "card",
				content: _(
					"Predicted equity at year end is displayed on the top left. To visualize future solvency situations you also find an Equity ratio trend graph.",
				),
			},
		];
	}

	_makeHelpStepsForCashFlowReport() {
		return [
			{
				title: _("Cash at hand"),
				element: "card",
				content: _(
					"The top of this page shows you the predicted low point of cash for predicted periods. As negative cash usually prevents a company from continuing its operations, the “top card” will become red as a warning if a negative cash balance is predicted to occur in the future.The cash balance graph also should show you the future expected level of cash at hand.",
				),
			},
			{
				title: _("Flows of cash"),
				element: "predicted-clickable-non-account-cell",
				content: (html) =>
					html.div(
						html.p(
							_(
								"This table of accounts shows which account types that have effected cash at hand.",
							),
						),
						html.p(
							_(
								"These are grouped under Net operations, Changes in current assets, Changes in fixed assets and Changes in liabilities.This is view only as predictions are made from the Balance sheet or the Profit and loss.",
							),
						),
					),
			},
		];
	}
}
